import React, { useState, useEffect, useCallback } from 'react';
import { Umbrella, Plus, ChevronDown, ChevronUp, Play, CheckCircle, X } from 'lucide-react';
import * as dpService from 'features/dp/services/dpFeriasService';
import FeriasVencimentos from './FeriasVencimentos';

// Mapeamento de status para badge
const STATUS_FERIAS = {
  agendada: { label: 'Agendada',  color: 'bg-blue-100 text-blue-700' },
  em_gozo:  { label: 'Em Gozo',   color: 'bg-yellow-100 text-yellow-700' },
  concluida:{ label: 'Concluída', color: 'bg-green-100 text-green-700' },
  cancelada:{ label: 'Cancelada', color: 'bg-gray-100 text-gray-500' },
};

function formatarData(iso) {
  if (!iso) return '—';
  const [ano, mes, dia] = String(iso).substring(0, 10).split('-');
  return ano && mes && dia ? `${dia}/${mes}/${ano}` : iso;
}

function formatarMoeda(valor) {
  return Number(valor || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

const inputClass =
  'w-full px-3 py-2 text-sm rounded-xl border border-[color:var(--border)] bg-[color:var(--surface)] text-[color:var(--text)] focus:outline-none focus:ring-2 focus:ring-teal-500';

function CampoForm({ label, children, span }) {
  return (
    <div className={span ? 'sm:col-span-2' : ''}>
      <label className="block text-xs font-medium text-[color:var(--text-muted)] mb-1">{label}</label>
      {children}
    </div>
  );
}

export default function FeriasTab({ podeEditar }) {
  const [registros, setRegistros] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const [mostrarForm, setMostrarForm] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [funcionarios, setFuncionarios] = useState([]);
  const [form, setForm] = useState({
    funcionario_id: '',
    periodo_aquisitivo_inicio: '',
    periodo_aquisitivo_fim: '',
    gozo_inicio: '',
    dias_gozo: '30',
    dias_abono: '0',
    faltas_injustificadas: '0',
    observacoes: '',
  });

  // ── Carregamento ───────────────────────────────────────────
  const carregarFerias = useCallback(async () => {
    setCarregando(true);
    try {
      const res = await dpService.listarFerias();
      setRegistros(res?.data?.data?.data ?? []);
    } catch {
      setErro('Erro ao carregar férias');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    carregarFerias();
    dpService.listarFerias({ status: 'ativo', per_page: 200 })
      .then(res => setFuncionarios(res?.data?.data?.data ?? []))
      .catch(() => {});
  }, [carregarFerias]);

  // ── Ações ──────────────────────────────────────────────────
  const executarAcao = async (fn) => {
    try {
      await fn();
      await carregarFerias();
    } catch (e) {
      setErro(e?.response?.data?.message ?? 'Erro');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSalvando(true);
    setErro(null);
    try {
      // Usa a API via axios diretamente pelo serviço
      const api = (await import('services/api')).default;
      await api.post('/dp/ferias', form);
      setMostrarForm(false);
      setForm({
        funcionario_id: '', periodo_aquisitivo_inicio: '', periodo_aquisitivo_fim: '',
        gozo_inicio: '', dias_gozo: '30', dias_abono: '0', faltas_injustificadas: '0', observacoes: '',
      });
      await carregarFerias();
    } catch (e) {
      setErro(e?.response?.data?.message ?? 'Erro ao salvar férias');
    } finally {
      setSalvando(false);
    }
  };

  const setField = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));

  // ── Render ─────────────────────────────────────────────────
  return (
    <div className="space-y-5">

      {/* Cabeçalho */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Umbrella size={20} className="text-teal-600" />
          <h2 className="text-base font-bold text-[color:var(--text)]">Férias</h2>
        </div>
        {podeEditar && (
          <button
            onClick={() => setMostrarForm(v => !v)}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-xl bg-teal-600 text-white hover:bg-teal-700 transition-colors"
          >
            <Plus size={14} />
            Agendar Férias
            {mostrarForm ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
        )}
      </div>

      {/* Mensagem de erro */}
      {erro && (
        <div className="text-sm text-red-600 bg-red-50 dark:bg-red-900/20 p-3 rounded-xl">{erro}</div>
      )}

      {/* ── Vencimentos ── NOVO ────────────────────────────── */}
      <FeriasVencimentos />

      {/* Formulário de agendamento */}
      {mostrarForm && (
        <form
          onSubmit={handleSubmit}
          className="border border-[color:var(--border)] rounded-2xl p-5 space-y-4 bg-[color:var(--surface-muted)]"
        >
          <h3 className="text-sm font-semibold text-[color:var(--text)]">Novo Agendamento de Férias</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <CampoForm label="Funcionário *">
              <select required value={form.funcionario_id} onChange={setField('funcionario_id')} className={inputClass}>
                <option value="">Selecione...</option>
                {funcionarios.map(f => (
                  <option key={f.id} value={f.id}>{f.pessoa?.nome} — {f.matricula}</option>
                ))}
              </select>
            </CampoForm>
            <CampoForm label="Período aquisitivo — início *">
              <input type="date" required value={form.periodo_aquisitivo_inicio} onChange={setField('periodo_aquisitivo_inicio')} className={inputClass} />
            </CampoForm>
            <CampoForm label="Período aquisitivo — fim *">
              <input type="date" required value={form.periodo_aquisitivo_fim} onChange={setField('periodo_aquisitivo_fim')} className={inputClass} />
            </CampoForm>
            <CampoForm label="Início do gozo *">
              <input type="date" required value={form.gozo_inicio} onChange={setField('gozo_inicio')} className={inputClass} />
            </CampoForm>
            <CampoForm label="Dias de gozo">
              <input type="number" min="1" max="30" value={form.dias_gozo} onChange={setField('dias_gozo')} className={inputClass} />
            </CampoForm>
            <CampoForm label="Dias abono pecuniário (máx 10)">
              <input type="number" min="0" max="10" value={form.dias_abono} onChange={setField('dias_abono')} className={inputClass} />
            </CampoForm>
            <CampoForm label="Faltas injustificadas no período">
              <input type="number" min="0" value={form.faltas_injustificadas} onChange={setField('faltas_injustificadas')} className={inputClass} />
            </CampoForm>
            <CampoForm label="Observações" span>
              <input type="text" value={form.observacoes} onChange={setField('observacoes')} className={inputClass} />
            </CampoForm>
          </div>
          <p className="text-xs text-[color:var(--text-muted)]">
            Dias de direito: até 5 faltas = 30 dias | 6–14 = 24 dias | 15–23 = 18 dias | 24–32 = 12 dias
          </p>
          <div className="flex gap-3 justify-end">
            <button type="button" onClick={() => setMostrarForm(false)}
              className="px-4 py-2 text-sm rounded-xl border border-[color:var(--border)] text-[color:var(--text-muted)] hover:bg-[color:var(--surface)] transition-colors">
              Cancelar
            </button>
            <button type="submit" disabled={salvando}
              className="px-4 py-2 text-sm font-semibold rounded-xl bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-60 transition-colors">
              {salvando ? 'Calculando...' : 'Calcular e Agendar'}
            </button>
          </div>
        </form>
      )}

      {/* Tabela de registros */}
      {carregando ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin w-6 h-6 border-2 border-teal-600 border-t-transparent rounded-full" />
        </div>
      ) : registros.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Umbrella size={40} className="text-[color:var(--text-muted)] opacity-30 mb-3" />
          <p className="text-sm text-[color:var(--text-muted)]">Nenhum registro de férias encontrado.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-[color:var(--border)]">
          <table className="w-full text-sm">
            <thead className="bg-[color:var(--surface-muted)]">
              <tr>
                {['Funcionário', 'Período Aquisitivo', 'Gozo', 'Dias', 'Valor Férias', 'INSS', 'IRRF', 'Líquido', 'Status', 'Ações'].map(col => (
                  <th key={col} className="text-left px-4 py-3 text-xs font-semibold text-[color:var(--text-muted)] whitespace-nowrap">{col}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[color:var(--border)]">
              {registros.map(reg => {
                const badge = STATUS_FERIAS[reg.status] ?? {};
                return (
                  <tr key={reg.id} className="hover:bg-[color:var(--surface-muted)] transition-colors">
                    <td className="px-4 py-3 font-medium text-[color:var(--text)] whitespace-nowrap">
                      {reg.funcionario?.pessoa?.nome}
                    </td>
                    <td className="px-4 py-3 text-[color:var(--text-muted)] whitespace-nowrap">
                      {formatarData(reg.periodo_aquisitivo_inicio)} – {formatarData(reg.periodo_aquisitivo_fim)}
                    </td>
                    <td className="px-4 py-3 text-[color:var(--text-muted)] whitespace-nowrap">
                      {formatarData(reg.gozo_inicio)} – {formatarData(reg.gozo_fim)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {reg.dias_gozo}{reg.dias_abono > 0 ? `+${reg.dias_abono}ab` : ''}
                    </td>
                    <td className="px-4 py-3 text-right text-green-600 font-medium">
                      {formatarMoeda(Number(reg.valor_ferias) + Number(reg.valor_adicional_terco))}
                    </td>
                    <td className="px-4 py-3 text-right text-red-500">{formatarMoeda(reg.valor_inss)}</td>
                    <td className="px-4 py-3 text-right text-red-500">{formatarMoeda(reg.valor_irrf)}</td>
                    <td className="px-4 py-3 text-right font-semibold text-teal-600">{formatarMoeda(reg.valor_liquido)}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${badge.color}`}>
                        {badge.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {podeEditar && (
                        <div className="flex gap-1">
                          {reg.status === 'agendada' && (
                            <>
                              <button
                                onClick={() => executarAcao(async () => { const a = (await import('services/api')).default; await a.post(`/dp/ferias/${reg.id}/iniciar-gozo`); })}
                                title="Iniciar gozo"
                                className="p-1.5 rounded-lg text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/20 transition-colors"
                              >
                                <Play size={14} />
                              </button>
                              <button
                                onClick={() => executarAcao(async () => { const a = (await import('services/api')).default; await a.delete(`/dp/ferias/${reg.id}`); })}
                                title="Cancelar"
                                className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                              >
                                <X size={14} />
                              </button>
                            </>
                          )}
                          {reg.status === 'em_gozo' && (
                            <button
                              onClick={() => executarAcao(async () => { const a = (await import('services/api')).default; await a.post(`/dp/ferias/${reg.id}/concluir-gozo`); })}
                              title="Registrar retorno"
                              className="p-1.5 rounded-lg text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
                            >
                              <CheckCircle size={14} />
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
